import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        titleTypographyProps: {
            variant: "headerSecondary",
            color: "text.accent",
            fontFamily: "inherit",
            css: css`
                text-transform: capitalize;
            `,
        },
        headerProps: {
            css: css`
                &:not(:nth-of-type(1)) {
                    margin-top: 5px;
                }
                &:nth-of-type(1) {
                    button {
                        border-top-left-radius: 6px;
                        border-top-right-radius: 6px;
                    }
                }
                button {
                    border: none;
                    background: ${getColor("secondary")};
                    padding: 15px 20px 3px 20px;
                    &:focus {
                        border: none !important;
                        padding: 15px 20px 3px 20px;
                        padding-top: 15px !important;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                &:last-of-type {
                    border-bottom-left-radius: 6px;
                    border-bottom-right-radius: 6px;
                }
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                background: ${getColor("secondary")};
                border: none;
                padding: 15px 20px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            [class^="TypographyStyle"] {
                margin-top: 0 !important;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            [class^="IconWrapper"] {
                background: white;
            }
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            variant: "p",
            css: css`
                font-size: 15px;
                word-break: break-word;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                font-size: 15px;
                word-break: break-word;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface FacetModel {
    name: string;
    id?: string;
    filterType: string;
}

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    selectedFilterNamesArr?: string[];
    isPrice?: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    extendedStyles,
    selectedFilterNamesArr,
    isPrice,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const addDollarSign = (value: string) => {
        const values = value.split("-");
        return `$ ${values[0]} - $ ${values[1]}`;
    };
    const sortedFacets = !isPrice ? [...facets].sort((a, b) => a.name.localeCompare(b.name)) : facets;

    return (
        <ManagedAccordionSection title={title} {...styles.accordionSection} initialExpanded={true}>
            {sortedFacets?.map(facet => (
                <CheckboxGroup key={facet.name} {...styles.checkBoxGroup}>
                    <Checkbox
                        {...(selectedFilterNamesArr?.includes(facet.name.toLowerCase())
                            ? styles.checkBoxSelected
                            : styles.checkBox)}
                        checked={selectedFilterNamesArr?.includes(facet.name.toLowerCase())}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.name}`}
                    >
                        {facet.filterType === "price" ? addDollarSign(facet.name) : facet.name}
                    </Checkbox>
                </CheckboxGroup>
            ))}
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
